import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Calendar, Sparkles, ChevronLeft, ChevronRight, BadgeCheck, UserRoundSearch } from 'lucide-react';
import specialtyService from '../services/specialtyService';
import { doctorService } from '../services/doctorService';
import GlobalLoader from './GlobalLoader';
import DoctorIdentityCard from './DoctorIdentityCard';
import Footer from './Footer';
import florLogo from '../assets/florDaisiesSF.png';
import letrasLogo from '../assets/daisies_letras.png';
import { getUserSession } from '../utils/checkoutHelpers';

// ─── Inline mini-schedule for a doctor ───
const DoctorScheduleInline = ({ doctorId, onSlotClick, specialty }) => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchSchedule = async () => {
      try {
        const authToken = localStorage.getItem('auth_token') || 'public';
        const doctorTz = 'America/Mexico_City';

        // Build durations list from the specialty (same pattern as DoctorSelection)
        const includedDurations = (specialty?.durations || []).filter(d => d && d.id && !d.is_extra_duration);
        const extraDurations = specialty?.extra_durations || [];
        const allDurations = [
          ...includedDurations.map(d => ({ id: d.id, isExtra: false, durationMinutes: d.duration })),
          ...extraDurations.map(d => ({ id: d.id, isExtra: true, durationMinutes: d.duration })),
        ];

        // Fallback if no durations found
        if (allDurations.length === 0) {
          const fallbackId = specialty?.durationId || 109;
          allDurations.push({ id: fallbackId, isExtra: false, durationMinutes: null });
          if (specialty?.extraDurationId && specialty.extraDurationId !== fallbackId) {
            allDurations.push({ id: specialty.extraDurationId, isExtra: true, durationMinutes: null });
          }
        }

        const result = await doctorService.getDoctorScheduleAllDurations(
          doctorId, authToken, doctorTz, allDurations
        );
        if (!cancelled && result?.data) {
          setSchedule(result.data);
        }
      } catch (err) {
        console.warn('[DoctorScheduleInline] Error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSchedule();
    return () => { cancelled = true; };
  }, [doctorId, specialty]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="w-5 h-5 border-2 border-daisies border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!schedule || schedule.length === 0) {
    return (
      <p className="text-text-secondary text-xs py-4 text-center">Sin horarios disponibles</p>
    );
  }

  // Show up to 4 days
  const dayLabels = ['Hoy', 'Mañana'];

  const formatDayLabel = (dateStr, idx) => {
    if (idx < dayLabels.length) return dayLabels[idx];
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' });
  };

  const formatTime = (hour) => {
    const [h, m] = hour.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    return `${hr % 12 || 12}:${m} ${ampm}`;
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (startIndex + 4 < schedule.length) {
      setStartIndex(startIndex + 1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (startIndex > 0) {
      setStartIndex(startIndex - 1);
    }
  };

  // Show a window of 4 days
  const days = schedule.slice(startIndex, startIndex + 4);

  return (
    <div className="relative group/schedule">
      {/* Navigation Arrows */}
      {startIndex > 0 && (
        <button
          onClick={handlePrev}
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-elevated flex items-center justify-center text-daisies hover:bg-daisies-50 transition-all press-scale"
        >
          <ChevronLeft size={16} />
        </button>
      )}
      
      {startIndex + 4 < schedule.length && (
        <button
          onClick={handleNext}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-elevated flex items-center justify-center text-daisies hover:bg-daisies-50 transition-all press-scale"
        >
          <ChevronRight size={16} />
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {days.map((day, idx) => {
          const available = (day.schedule_available || []).filter(s => s.available);
          const currentIdx = startIndex + idx;
          return (
            <div key={day.date} className="flex flex-col">
              <span className="text-xs font-semibold text-text-secondary mb-1.5 text-center truncate">
                {formatDayLabel(day.date, currentIdx)}
              </span>
              <div className="flex flex-col gap-1">
                {available.length === 0 ? (
                  <span className="text-[10px] text-text-secondary text-center py-1">—</span>
                ) : (
                  available.slice(0, 3).map((slot) => (
                    <button
                      key={`${slot.id}-${slot.hour}-${slot.duration_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSlotClick({
                          ...slot,
                          date: day.date,
                          week_day: day.week_day,
                          init: slot.hour + ':00',
                        });
                      }}
                      className="press-scale text-[11px] font-medium px-2 py-1.5 rounded-lg bg-daisies-50 text-daisies hover:bg-daisies hover:text-white transition-all text-center"
                    >
                      {formatTime(slot.hour)}
                    </button>
                  ))
                )}
                {available.length > 3 && (
                  <span className="text-[10px] text-daisies font-medium text-center">+{available.length - 3} más</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Doctor card with inline schedule ───
const DirectoryDoctorCard = ({ doctor, onSlotClick, specialtyId, specialty, onOndemandRequest, highlighted = false }) => {
  return (
    <div
      id={`doctor-card-${doctor.id}`}
      className={`bg-white rounded-card transition-all animate-fadeIn ${
        highlighted ? 'ring-2 ring-daisies relative' : ''
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-5 inline-flex items-center gap-1.5 bg-daisies text-white text-xs font-bold rounded-full px-3 py-1 shadow-elevated">
          <BadgeCheck size={13} aria-hidden="true" />
          Tu match recomendado
        </div>
      )}
      <div className="flex flex-col lg:flex-row">
        {/* Doctor info (left side) */}
        <div className="flex-1 p-5 lg:p-6">
          <DoctorIdentityCard doctor={doctor} size="standard" />
        </div>

        {/* Inline schedule (right side) */}
        <div className="lg:w-[340px] border-t lg:border-t-0 lg:border-l border-brand-100 p-4 lg:p-5 bg-surface/40">
          <p className="text-xs font-semibold text-text-secondary mb-2 flex items-center gap-1.5">
            <Calendar size={13} className="text-daisies" />
            Horarios disponibles
          </p>
          <DoctorScheduleInline
            doctorId={doctor.id}
            specialty={specialty}
            onSlotClick={(slot) => {
              onSlotClick(specialtyId, {
                ...slot,
                doctor: doctor,
                professional_ids: [{ id: doctor.id }],
                professional_id: doctor.id,
              });
            }}
          />
          
          {onOndemandRequest && (
            <button
              onClick={() => onOndemandRequest(doctor)}
              className="w-full mt-4 py-2.5 px-4 border-[1.5px] border-brand text-daisies rounded-xl font-bold hover:bg-daisies-50 transition-all flex items-center justify-center gap-2 text-xs group"
            >
              <Sparkles size={14} className="group-hover:rotate-12 transition-transform" />
              Proponer horario SOS
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main page ───
const DoctorDirectoryPage = ({ onStartBooking, onLoginClick, onOndemandRequest }) => {
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(16); // Psicología
  const [specialtyConfig, setSpecialtyConfig] = useState(null);
  const [consultationType, setConsultationType] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);

  // Deep-link: ?doctor=<id>&specialty=<name>&doctor_name=<first>
  // Cuando viene del landing /tests con match recomendado, aterrizamos
  // directo en esa doctora con scroll + highlight visual.
  const [highlightedDoctorId, setHighlightedDoctorId] = useState(null);
  const initialParamsRef = useRef(null);
  const scrollDoneRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const doctorParam = params.get('doctor');
    const specialtyParam = params.get('specialty');
    const doctorNameParam = params.get('doctor_name');
    if (doctorParam) {
      const id = parseInt(doctorParam, 10);
      if (!isNaN(id)) setHighlightedDoctorId(id);
    }
    initialParamsRef.current = {
      doctor: doctorParam,
      specialty: specialtyParam,
      doctorName: doctorNameParam,
    };
  }, []);

  // Cuando carguen las especialidades, alinear selectedSpecialty si el
  // param trae una especialidad nombrada (p. ej. "Psicología").
  useEffect(() => {
    const param = initialParamsRef.current?.specialty;
    if (!param || specialties.length === 0) return;
    const norm = param.trim().toLowerCase();
    const match = specialties.find(
      (s) => (s.name || '').trim().toLowerCase() === norm,
    );
    if (match && match.id !== selectedSpecialty) {
      setSelectedSpecialty(match.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialties]);

  // Cuando los doctors carguen y el highlighted esté en la lista, scrollear.
  // Solo una vez por sesión, no en cambios de filtro posteriores.
  useEffect(() => {
    if (scrollDoneRef.current) return;
    if (!highlightedDoctorId || doctors.length === 0) return;
    if (!doctors.find((d) => d.id === highlightedDoctorId)) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`doctor-card-${highlightedDoctorId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrollDoneRef.current = true;
      }
    }, 400);
    return () => clearTimeout(t);
  }, [doctors, highlightedDoctorId]);

  // Fetch specialties on mount
  useEffect(() => {
    const load = async () => {
      try {
        const specs = await specialtyService.getFormattedOnDemandSpecialties();
        setSpecialties(specs);
      } catch (err) {
        console.warn('[DoctorDirectoryPage] Error loading specialties:', err);
      }
    };
    load();
  }, []);

  // Load full specialty config (includes durations)
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const configMap = await specialtyService.getSpecialtyConfig(selectedSpecialty);
        // getSpecialtyConfig returns { 16: {...}, 15: {...}, ... }
        const config = configMap?.[selectedSpecialty] || null;
        setSpecialtyConfig(config);
      } catch (err) {
        console.warn('[DoctorDirectoryPage] Error loading specialty config:', err);
        setSpecialtyConfig(null);
      }
    };
    loadConfig();
  }, [selectedSpecialty]);

  // Fetch doctors when specialty changes
  useEffect(() => {
    let cancelled = false;
    const loadDrs = async () => {
      setLoading(true);
      try {
        const data = await doctorService.getDoctorsBySpecialty(selectedSpecialty);
        if (!cancelled) {
          const sorted = (data || []).sort((a, b) =>
            b.rating_professional - a.rating_professional || b.consultation_minutes - a.consultation_minutes
          );
          setDoctors(sorted);
        }
      } catch (err) {
        console.warn('[DoctorDirectoryPage] Error loading doctors:', err);
        if (!cancelled) setDoctors([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDrs();
    return () => { cancelled = true; };
  }, [selectedSpecialty]);

  // Filter doctors by search
  const filteredDoctors = doctors.filter(doc => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      `${doc.name} ${doc.last_name}`.toLowerCase().includes(term) ||
      doc.tag_ids?.some(t => t.name.toLowerCase().includes(term))
    );
  });

  const currentSpecialty = specialties.find(s => s.id === selectedSpecialty);

  // Generate date range pills
  const dateRanges = [
    { key: 'today', label: 'Hoy' },
    { key: 'tomorrow', label: 'Mañana' },
    { key: 'week', label: 'Esta semana' },
  ];

  return (
    <>
      {/* Navbar */}
      <div className="bg-white/80 backdrop-blur-md border-b border-brand-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={florLogo} alt="Daisies" className="h-9 w-auto" />
            <img src={letrasLogo} alt="Daisies" className="h-6 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-text-secondary hover:text-daisies transition-colors font-medium">
              Inicio
            </a>
            <a href="/horarios" className="text-sm text-text-secondary hover:text-daisies transition-colors font-medium">
              Horarios
            </a>
            {getUserSession() ? (
              <a
                href="/"
                className="press-scale px-5 py-2 bg-daisies text-white rounded-full hover:opacity-90 transition-all text-sm font-medium"
              >
                Mi cuenta
              </a>
            ) : (
              <button
                onClick={onLoginClick}
                className="press-scale px-5 py-2 text-daisies border-[1.5px] border-brand rounded-full hover:bg-daisies hover:text-white transition-all text-sm font-medium"
              >
                Iniciar Sesión
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-brand-tint pt-12 pb-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-3">
            Encuentra a tu <span className="text-daisies">especialista</span> en línea
          </h1>
          <p className="text-text-secondary text-base md:text-lg max-w-2xl mx-auto mb-8">
            Más de 300 profesionales certificadas listas para atenderte desde la comodidad de tu hogar
          </p>

          {/* Filter bar */}
          <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-elevated p-2 flex flex-col sm:flex-row gap-2 items-stretch">
            {/* Specialty dropdown */}
            <div className="relative flex-1">
              <button
                onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
                className="w-full px-4 py-3 rounded-xl bg-surface hover:bg-daisies-50 transition-all text-left flex items-center justify-between gap-2"
              >
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block">Especialidad</span>
                  <span className="text-sm font-semibold text-text-primary">
                    {currentSpecialty?.name || 'Psicología'}
                  </span>
                </div>
                <ChevronDown size={16} className={`text-text-secondary transition-transform ${showSpecialtyDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showSpecialtyDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-elevated z-50 max-h-64 overflow-y-auto">
                  {specialties.map(spec => (
                    <button
                      key={spec.id}
                      onClick={() => {
                        setSelectedSpecialty(spec.id);
                        setShowSpecialtyDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-daisies-50 transition-colors flex items-center gap-2 ${
                        spec.id === selectedSpecialty ? 'bg-daisies-50 text-daisies font-semibold' : 'text-text-primary'
                      }`}
                    >                      <span>{spec.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px bg-brand-100 my-2" />

            {/* Consultation type */}
            <div className="flex-1">
              <div className="px-4 py-3 rounded-xl bg-surface">
                <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block">Tipo de consulta</span>
                <select
                  value={consultationType}
                  onChange={(e) => setConsultationType(e.target.value)}
                  className="text-sm font-semibold text-text-primary bg-transparent w-full outline-none cursor-pointer"
                >
                  <option value="all">Todas</option>
                  <option value="included">Incluida</option>
                  <option value="extra">Extra</option>
                </select>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px bg-brand-100 my-2" />

            {/* Date range */}
            <div className="flex-1">
              <div className="px-4 py-3 rounded-xl bg-surface">
                <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block">Fecha</span>
                <div className="flex gap-1 mt-0.5">
                  {dateRanges.map(dr => (
                    <button
                      key={dr.key}
                      onClick={() => setSelectedDateRange(dr.key)}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all ${
                        selectedDateRange === dr.key
                          ? 'bg-daisies text-white'
                          : 'bg-white text-text-secondary hover:bg-daisies-50'
                      }`}
                    >
                      {dr.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Search button */}
            <button
              onClick={() => {/* Filters already applied reactively */}}
              className="press-scale px-6 py-3 bg-daisies text-white rounded-xl font-semibold hover:bg-daisies-hover transition-all flex items-center gap-2 justify-center text-sm"
            >
              <Search size={16} />
              <span className="hidden sm:inline">Buscar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Doctor listing */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Name search + count */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-xl font-bold text-text-primary tracking-tight">
              Especialistas en {currentSpecialty?.name || 'Psicología'}
            </h2>
            <p className="text-sm text-text-secondary mt-0.5">
              {filteredDoctors.length} {filteredDoctors.length === 1 ? 'doctora disponible' : 'doctoras disponibles'}
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-warm-canvas rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-all"
            />
          </div>
        </div>

        {/* Loading state */}
        {loading ? (
          <GlobalLoader
            title={`Buscando especialistas en ${currentSpecialty?.name || 'Psicología'}`}
            subtitle="Cargando las mejores doctoras para ti..."
            overlay={false}
          />
        ) : filteredDoctors.length === 0 ? (
          <div className="text-center py-16 bg-surface-card rounded-card">
            <UserRoundSearch className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">No se encontraron doctoras</h3>
            <p className="text-text-secondary text-sm">
              {searchTerm ? 'Intenta con otro término de búsqueda.' : 'No hay doctoras disponibles en esta especialidad.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDoctors.map(doctor => (
              <DirectoryDoctorCard
                key={doctor.id}
                doctor={doctor}
                specialtyId={selectedSpecialty}
                specialty={specialtyConfig}
                onSlotClick={onStartBooking}
                onOndemandRequest={onOndemandRequest}
                highlighted={doctor.id === highlightedDoctorId}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </>
  );
};

export default DoctorDirectoryPage;
export { DoctorScheduleInline };
